import { fetchMessages, submitMessage, streamConversation, ensureConversation } from '@/apis/AIGuidance/repositories/conversation.repository'
import { createLocalId } from '@/utils/AIGuidance'

function normalizeMessageContent(content) {
  return (content || '').replace(/\s+/g, ' ').trim()
}

function getMessageTime(message) {
  const value = new Date(message.createdAt).getTime()
  return Number.isNaN(value) ? 0 : value
}

function isClientEchoMessage(message) {
  const raw = message.raw
  if (raw && typeof raw === 'object' && raw.optimistic === true) {
    return true
  }
  return /^(user|local)-/.test(message.id) && !message.raw
}

function isSameLogicalMessage(current, incoming) {
  if (current.role !== incoming.role) return false
  if (normalizeMessageContent(current.content) !== normalizeMessageContent(incoming.content)) return false

  const currentConversationId = current.conversationId === 'pending-conversation' ? '' : current.conversationId
  const incomingConversationId = incoming.conversationId === 'pending-conversation' ? '' : incoming.conversationId

  if (currentConversationId && incomingConversationId && currentConversationId !== incomingConversationId) {
    return false
  }

  return Math.abs(getMessageTime(current) - getMessageTime(incoming)) <= 2 * 60 * 1000
}

function mergeMessageLists(existing, incoming) {
  const merged = [...existing]

  for (const message of incoming) {
    const sameIdIndex = merged.findIndex((item) => item.id === message.id)
    if (sameIdIndex >= 0) {
      merged[sameIdIndex] = message
      continue
    }

    const echoIndex = merged.findIndex(
      (item) => isClientEchoMessage(item) && isSameLogicalMessage(item, message)
    )
    if (echoIndex >= 0) {
      merged[echoIndex] = message
      continue
    }

    merged.push(message)
  }

  return sortMessages(merged)
}

function sortMessages(messages) {
  return [...messages].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
}

function cloneTraceSteps(traceSteps = []) {
  return traceSteps.map((step) => ({ ...step }))
}

function appendTraceContent(current, incoming) {
  const next = (incoming || '').trim()
  if (!next) return current || ''
  if (!current) return next
  if (next.startsWith(current)) return next
  if (current.endsWith(next)) return current
  return `${current}\n${next}`
}

function resolveActiveTraceIndex(traceSteps, patch) {
  for (let index = traceSteps.length - 1; index >= 0; index -= 1) {
    const step = traceSteps[index]
    if (step.kind !== patch.kind || step.status !== 'active') continue
    if (patch.kind === 'tool' && step.title !== patch.title) continue
    return index
  }
  return -1
}

function applyTracePatch(traceSteps = [], patch) {
  const nextSteps = cloneTraceSteps(traceSteps)
  const activeIndex = resolveActiveTraceIndex(nextSteps, patch)

  if (patch.phase === 'start') {
    if (activeIndex >= 0) {
      nextSteps[activeIndex] = { ...nextSteps[activeIndex], status: 'completed' }
    }

    nextSteps.push({
      id: createLocalId(`trace-${patch.kind}`),
      kind: patch.kind,
      title: patch.title,
      content: (patch.content || '').trim(),
      status: patch.status,
    })
    return nextSteps
  }

  if (activeIndex >= 0) {
    nextSteps[activeIndex] = {
      ...nextSteps[activeIndex],
      title: patch.title || nextSteps[activeIndex].title,
      content: appendTraceContent(nextSteps[activeIndex].content, patch.content),
      status: patch.status,
    }
    return nextSteps
  }

  nextSteps.push({
    id: createLocalId(`trace-${patch.kind}`),
    kind: patch.kind,
    title: patch.title,
    content: (patch.content || '').trim(),
    status: patch.status,
  })

  return nextSteps
}

function commitReasoningBuffer(traceSteps = [], content) {
  const next = (content || '').trim()
  if (!next) return cloneTraceSteps(traceSteps)

  const nextSteps = cloneTraceSteps(traceSteps)
  for (let index = nextSteps.length - 1; index >= 0; index -= 1) {
    const step = nextSteps[index]
    if (step.kind !== 'reasoning') continue

    nextSteps[index] = {
      ...step,
      content: appendTraceContent(step.content, next),
      status: 'completed',
    }
    return nextSteps
  }

  nextSteps.push({
    id: createLocalId('trace-reasoning'),
    kind: 'reasoning',
    title: '分析中',
    content: next,
    status: 'completed',
  })
  return nextSteps
}

const state = {
  conversationId: '',
  agentId: '',
  depType: 1,
  messages: [],
  nextPage: undefined,
  hasMore: false,
  ui: {
    loadingInitial: false,
    loadingMore: false,
    sending: false,
    error: null,
    isAtBottom: true,
    showJumpToBottom: false,
    draft: '',
  }
}

const getters = {
  reversedMessages(state) {
    return state.messages
  },
  latestMessagePreview(state) {
    return state.messages[state.messages.length - 1]?.content || '开始新的对话'
  }
}

const mutations = {
  RESET_STATE(state) {
    state.conversationId = ''
    state.messages = []
    state.nextPage = undefined
    state.hasMore = false
    state.ui = {
      loadingInitial: false,
      loadingMore: false,
      sending: false,
      error: null,
      isAtBottom: true,
      showJumpToBottom: false,
      draft: '',
    }
  },
  SET_CONVERSATION_ID(state, id) {
    state.conversationId = id
    if (typeof sessionStorage !== 'undefined') {
      if (state.agentId && id) {
        sessionStorage.setItem(`AIGUIDANCE_CONVERSATION_${state.agentId}_${state.depType || 1}`, id)
      }
      if (state.agentId && !id) {
        sessionStorage.removeItem(`AIGUIDANCE_CONVERSATION_${state.agentId}_${state.depType || 1}`)
      }
    }
  },
  SET_AGENT_ID(state, id) {
    state.agentId = id
  },
  SET_DEP_TYPE(state, value) {
    state.depType = value === 2 ? 2 : 1
  },
  SET_MESSAGES(state, messages) {
    state.messages = sortMessages(messages)
  },
  MERGE_MESSAGES(state, incoming) {
    state.messages = mergeMessageLists(state.messages, incoming)
  },
  PREPEND_MESSAGES(state, older) {
    const existingIds = new Set(state.messages.map((item) => item.id))
    const filtered = older.filter((item) => !existingIds.has(item.id))
    state.messages = sortMessages([...filtered, ...state.messages])
  },
  PUSH_MESSAGE(state, message) {
    state.messages = sortMessages([...state.messages, message])
  },
  UPDATE_MESSAGE(state, { id, payload }) {
    state.messages = state.messages.map(item => item.id === id ? { ...item, ...payload } : item)
  },
  REMOVE_MESSAGE(state, id) {
    state.messages = state.messages.filter(item => item.id !== id)
  },
  SET_PAGINATION(state, { hasMore, nextPage }) {
    state.hasMore = hasMore
    state.nextPage = nextPage
  },
  SET_UI(state, payload) {
    state.ui = { ...state.ui, ...payload }
  }
}

const actions = {
  setDraft({ commit }, value) {
    commit('SET_UI', { draft: value })
  },
  resetConversation({ commit }) {
    commit('RESET_STATE')
  },
  setAtBottom({ commit, state }, value) {
    commit('SET_UI', { 
      isAtBottom: value,
      showJumpToBottom: !value && state.messages.length > 0 
    })
  },
  clearError({ commit }) {
    commit('SET_UI', { error: null })
  },
  async loadInitial({ commit, state }) {
    if (!state.conversationId) {
      commit('SET_MESSAGES', [])
      commit('SET_PAGINATION', { hasMore: false, nextPage: undefined })
      return
    }

    commit('SET_UI', { loadingInitial: true, error: null })
    try {
      const result = await fetchMessages(state.conversationId, 1)
      commit('SET_MESSAGES', result.items)
      commit('SET_PAGINATION', { hasMore: result.hasMore, nextPage: result.nextPage })
    } catch (error) {
      commit('SET_UI', { error: error.message || '消息加载失败' })
    } finally {
      commit('SET_UI', { loadingInitial: false })
    }
  },
  async refreshLatest({ commit, state }) {
    if (!state.conversationId) return
    try {
      const result = await fetchMessages(state.conversationId, 1)
      commit('MERGE_MESSAGES', result.items)
      commit('SET_PAGINATION', { hasMore: result.hasMore, nextPage: result.nextPage })
    } catch (error) {
      console.error(error)
    }
  },
  async loadMore({ commit, state }) {
    if (!state.hasMore || state.ui.loadingMore || !state.nextPage) return

    commit('SET_UI', { loadingMore: true })
    try {
      const result = await fetchMessages(state.conversationId, state.nextPage)
      commit('PREPEND_MESSAGES', result.items)
      commit('SET_PAGINATION', { hasMore: result.hasMore, nextPage: result.nextPage })
    } catch (error) {
      commit('SET_UI', { error: error.message || '加载更多失败' })
    } finally {
      commit('SET_UI', { loadingMore: false })
    }
  },
  async sendDraft({ commit, state, dispatch }, content) {
    const trimmed = (content || '').trim()
    if (!trimmed || state.ui.sending) return

    const optimisticId = createLocalId('user')
    const userMessage = {
      id: optimisticId,
      conversationId: state.conversationId || 'pending-conversation',
      role: 'user',
      content: trimmed,
      contentType: 'text',
      createdAt: new Date().toISOString(),
      status: 'sending',
      raw: { optimistic: true },
    }

    commit('SET_UI', { sending: true, error: null, draft: '', isAtBottom: true, showJumpToBottom: false })
    commit('PUSH_MESSAGE', userMessage)

    const streamingId = createLocalId('streaming')

    try {
      const conversationId = await ensureConversation(state.conversationId, state.agentId, '') // thirdUserId 视情况传递
      if (state.conversationId !== conversationId) {
        commit('SET_CONVERSATION_ID', conversationId)
      }

      commit('UPDATE_MESSAGE', { id: optimisticId, payload: { conversationId, status: 'sent' } })

      commit('PUSH_MESSAGE', {
        id: streamingId,
        conversationId,
        role: 'assistant',
        content: '',
        contentType: 'markdown',
        createdAt: new Date().toISOString(),
        status: 'streaming',
        traceSteps: [],
        raw: { optimistic: true },
      })

      let streamedContent = ''

      await streamConversation(
        {
          message: trimmed,
          conversationId,
          agentId: state.agentId
        },
        {
          onToken: (token) => {
            streamedContent += token
            commit('UPDATE_MESSAGE', { id: streamingId, payload: { content: streamedContent } })
          },
          onTracePatch: (patch) => {
            if (patch.kind === 'reasoning' && patch.phase === 'start' && streamedContent.trim()) {
              commit('UPDATE_MESSAGE', {
                id: streamingId,
                payload: {
                  content: '',
                  traceSteps: commitReasoningBuffer(
                    (state.messages.find((item) => item.id === streamingId) || {}).traceSteps,
                    streamedContent,
                  ),
                },
              })
              streamedContent = ''
            }

            const current = state.messages.find((item) => item.id === streamingId)
            const nextTraceSteps = applyTracePatch((current && current.traceSteps) || [], patch)
            commit('UPDATE_MESSAGE', { id: streamingId, payload: { traceSteps: nextTraceSteps } })
          },
          onDone: () => {
            commit('UPDATE_MESSAGE', { id: streamingId, payload: { status: 'sent' } })
          },
          onError: (error) => {
            commit('UPDATE_MESSAGE', { id: streamingId, payload: { status: 'error', content: streamedContent + '\n\n[请求中断或发生错误]' } })
          }
        }
      )

      await dispatch('refreshLatest')

      // 在真实的逻辑中，刷新列表后，我们会拿到后端存的最新 assistant 消息。
      // 这里为了简单，我们只要把流式的占位符状态更新好，或者把它删掉，替换为真实 ID 的消息。
      // refreshLatest 会利用 mergeMessageLists，如果根据内容或者时间判断为同逻辑消息，会替换。
      // 所以我们直接删掉本地的 streaming 消息，如果它没被合并的话。
      const streamingMessage = state.messages.find(item => item.id === streamingId)
      if (streamingMessage) {
         // 没有被服务端合并，保留
         commit('UPDATE_MESSAGE', { id: streamingId, payload: { status: 'sent' } })
      }
    } catch (error) {
      commit('UPDATE_MESSAGE', { id: optimisticId, payload: { status: 'error' } })
      commit('SET_UI', { error: error.message || '发送失败' })
    } finally {
      commit('SET_UI', { sending: false })
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
